import { useMemo } from "react";
import {
  IUpsellingDataItem,
  IServiceCategoryCard,
  IVenueCard,
  isServiceCategoryCard,
  isVenueCard,
} from "../types/upsell.types";

export interface IUpsellMetrics {
  totalCategories: number;
  totalLeads: number;
  totalVenues: number;
  totalServices: number;
  activeCategories: number;
  activeVenues: number;
  bookedLeads: number;
  conversionRate: string;
}

export const useUpsellMetrics = (
  dashboardData: IUpsellingDataItem[] | null,
): IUpsellMetrics | null => {
  return useMemo(() => {
    if (!dashboardData || dashboardData?.length === 0) return null;

    // Filter service categories and venues using type guards
    const serviceCategories = dashboardData?.filter(
      isServiceCategoryCard,
    ) as IServiceCategoryCard[];
    const venues = dashboardData?.filter(isVenueCard) as IVenueCard[];

    const totalCategories = serviceCategories?.length;
    const totalVenues = venues?.length;

    // Calculate total leads from service categories
    const totalLeadsFromServices = serviceCategories.reduce(
      (acc, category) => acc + (category.leads?.length || 0),
      0,
    );

    // Calculate total leads from venues
    const totalLeadsFromVenues = venues.reduce(
      (acc, venue) => acc + (venue.leads?.length || 0),
      0,
    );

    const totalLeads = totalLeadsFromServices + totalLeadsFromVenues;

    // Calculate total unique services across all leads
    const totalServices = serviceCategories.reduce((acc, category) => {
      const leads = category.leads || [];
      // Count unique service items from upselling_services arrays
      const serviceCount = leads.reduce((leadAcc: number, lead) => {
        const upsellingServices = lead.upselling_services;
        if (Array.isArray(upsellingServices)) {
          return leadAcc + upsellingServices?.length;
        }
        return leadAcc;
      }, 0);
      return acc + serviceCount;
    }, 0);

    // Calculate active categories (check if any leads have ACTIVE status services)
    const activeCategories = serviceCategories.filter((cat) => {
      const leads = cat.leads || [];
      return leads.some((lead) => {
        const services = lead.upselling_services || [];
        return services.some((s) => s?.status === "ACTIVE");
      });
    })?.length;

    // Calculate active venues (check if any leads have ACTIVE status venues)
    const activeVenues = venues.filter((venue) => {
      const leads = venue.leads || [];
      return leads.some((lead) => lead.upselling_venue?.status === "ACTIVE");
    })?.length;

    // Calculate booked leads (leads with BOOKED status)
    const bookedLeadsFromServices = serviceCategories.reduce(
      (acc, category) => {
        return (
          acc +
          (category.leads?.filter((lead) => lead?.status === "BOOKED")
            ?.length || 0)
        );
      },
      0,
    );

    const bookedLeadsFromVenues = venues.reduce((acc, venue) => {
      return (
        acc +
        (venue.leads?.filter((lead) => lead?.status === "BOOKED")?.length || 0)
      );
    }, 0);

    const bookedLeads = bookedLeadsFromServices + bookedLeadsFromVenues;

    const conversionRate =
      totalLeads > 0 ? ((bookedLeads / totalLeads) * 100).toFixed(1) : "0";

    return {
      totalCategories,
      totalLeads,
      totalVenues,
      totalServices,
      activeCategories,
      activeVenues,
      bookedLeads,
      conversionRate,
    };
  }, [dashboardData]);
};
